import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });

export default function Approvals() {
  const [data, setData] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const navigate = useNavigate();

  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('npd_user')); } catch { return null; } })();
  const isApprover = currentUser?.role === 'approver';

  const load = () => API.get('/approvals/pending').then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const approveRecipe = async (id) => {
    await API.put(`/recipes/${id}/approve`, { approved_by: currentUser?.name || currentUser?.username });
    toast.success('Recipe approved!');
    load();
  };

  const approveCostSheet = async (id) => {
    await API.put(`/costsheet/versions/${id}/approve`, { approved_by: currentUser?.name || currentUser?.username });
    toast.success('Cost sheet approved!');
    load();
  };

  const reject = async () => {
    if (!rejectReason) return toast.error('Please provide a rejection reason');
    if (rejectTarget.type === 'recipe') {
      await API.put(`/recipes/${rejectTarget.id}/reject`, { rejection_reason: rejectReason });
    } else {
      await API.put(`/costsheet/versions/${rejectTarget.id}/reject`, { rejection_reason: rejectReason });
    }
    toast.success('Rejected and returned to draft');
    setShowRejectModal(false);
    setRejectReason('');
    load();
  };

  if (!isApprover) return (
    <div className="empty-state card">
      <div className="empty-state-icon">🔒</div>
      <p>You don't have approver access. Contact your admin.</p>
    </div>
  );

  if (!data) return <div className="empty-state"><p>Loading...</p></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Approvals</div>
          <div className="page-subtitle">{data.total} item{data.total !== 1 ? 's' : ''} pending approval</div>
        </div>
      </div>

      {data.total === 0 && (
        <div className="empty-state card">
          <div className="empty-state-icon">✓</div>
          <p>No pending approvals. All caught up!</p>
        </div>
      )}

      {/* Pending Recipes */}
      {data.recipes.length > 0 && (
        <div className="card mb-6">
          <div className="card-title">Recipes Pending Approval ({data.recipes.length})</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Product</th><th>Project</th><th>Submitted By</th><th>Date</th><th></th></tr>
              </thead>
              <tbody>
                {data.recipes.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.product_name}</div>
                      <div className="mono text-muted" style={{ fontSize: 10 }}>{r.product_code}</div>
                    </td>
                    <td className="text-muted">{r.project_name}</td>
                    <td>{r.confirmed_by || '—'}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{r.confirmed_at ? new Date(r.confirmed_at).toLocaleDateString('en-AE') : '—'}</td>
                    <td>
                      <div className="flex gap-2">
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/products/${r.product_id}`)}>View</button>
                        <button className="btn btn-primary btn-sm" onClick={() => approveRecipe(r.id)}>✓ Approve</button>
                        <button className="btn btn-danger btn-sm" onClick={() => { setRejectTarget({ id: r.id, type: 'recipe', name: r.product_name }); setShowRejectModal(true); }}>✕ Reject</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pending Cost Sheets */}
      {data.costSheets.length > 0 && (
        <div className="card mb-6">
          <div className="card-title">Cost Sheets Pending Approval ({data.costSheets.length})</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Product</th><th>Project</th><th>Doc No.</th><th>Version</th><th>Date</th><th></th></tr>
              </thead>
              <tbody>
                {data.costSheets.map(cs => (
                  <tr key={cs.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{cs.product_name}</div>
                      <div className="mono text-muted" style={{ fontSize: 10 }}>{cs.product_code}</div>
                    </td>
                    <td className="text-muted">{cs.project_name}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{cs.doc_number}</td>
                    <td className="mono">v{cs.version_number}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{new Date(cs.created_at).toLocaleDateString('en-AE')}</td>
                    <td>
                      <div className="flex gap-2">
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/products/${cs.product_id}`)}>View</button>
                        <button className="btn btn-primary btn-sm" onClick={() => approveCostSheet(cs.id)}>✓ Approve</button>
                        <button className="btn btn-danger btn-sm" onClick={() => { setRejectTarget({ id: cs.id, type: 'costsheet', name: cs.product_name }); setShowRejectModal(true); }}>✕ Reject</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Reject — {rejectTarget?.name}</div>
              <button className="modal-close" onClick={() => setShowRejectModal(false)}>×</button>
            </div>
            <div className="form-group">
              <label>Rejection Reason *</label>
              <textarea placeholder="Please provide reason for rejection..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={reject}>Reject & Return to Draft</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
